import React from 'react';
import type { ClassNameProp } from '../../common-types';
import { TableRow } from './TableRow';
import { TableHeader } from './TableHeader';
import { BlankState } from '../BlankState';
import type { CustomRow, TableProps } from '../Table';
import type { LanguageProperty } from '../../generated/LanguageProperty';

// eslint-disable-next-line @typescript-eslint/no-restricted-types
type DataType = object;
/**
 * Props for a {@link TableElement}.
 */
export type TableElementProps<RowDataType extends DataType> = ClassNameProp &
  Pick<
    TableProps<RowDataType>,
    | 'actions'
    | 'onRowClick'
    | 'onRowDoubleClick'
    | 'squashActions'
    | 'breakpoint'
    | 'sortable'
    | 'hideableColumns'
    | 'paging'
  > &
  Required<Pick<TableProps<RowDataType>, 'noDataIcon' | 'noDataMessage'>> & {
    /** The data that should be rendered. */
    data: Array<CustomRow<RowDataType>>;
    /** The tableInstance. */
    tableInstance: TableProps<RowDataType>['tableInstance'];
    /** If the blankState handling should be done by this component. */
    showBlankState: boolean;
    /** The caption for a table. If language keys are necessary this parameter contains intl(captionString). */
    caption: string;
    /**If sorting is enabled this aria-label is placed on the icon for descending*/
    ariaLabelDescending?: LanguageProperty;
    /**If sorting is enabled this aria-label is placed on the icon for ascending*/
    ariaLabelAscending?: LanguageProperty;
  };

/**
 * Component for the <table> element inside the {@link Table} component.
 *
 * @param parameters {@link TableElementProps}
 * @return the created {@link JSX.Element}.
 * @constructor
 */
export function TableElement<RowDataType extends DataType>({
  className,
  data,
  actions = [],
  squashActions,
  onRowClick,
  onRowDoubleClick,
  breakpoint,
  noDataIcon,
  noDataMessage,
  tableInstance,
  showBlankState,
  sortable,
  hideableColumns,
  paging,
  caption,
  ariaLabelAscending,
  ariaLabelDescending
}: TableElementProps<RowDataType>): JSX.Element {
  const {
    prepareRow,
    getTableBodyProps,
    allColumns,
    getTableProps,
    headerGroups
  } = tableInstance;
  return (
    <table {...getTableProps()} className={className}>
      <caption className="sr-only">{caption}</caption>
      <TableHeader
        headerGroups={headerGroups}
        hasActions={actions?.length > 0}
        sortable={sortable}
        allColumns={allColumns}
        hideableColumns={hideableColumns}
        paging={paging}
        ariaLabelAscending={ariaLabelAscending}
        ariaLabelDescending={ariaLabelDescending}
      />
      <tbody {...getTableBodyProps()}>
        {showBlankState && data.length === 0 && (
          <tr className="no-divider">
            <td
              colSpan={
                !!actions?.length ? allColumns.length + 1 : allColumns.length
              }
            >
              <BlankState iconName={noDataIcon} description={noDataMessage} />
            </td>
          </tr>
        )}
        {data.map((row: CustomRow<RowDataType>, rowIndex: number) => (
          <TableRow
            key={rowIndex}
            row={row}
            prepareRow={prepareRow}
            rowIndex={rowIndex}
            squashActions={squashActions}
            onRowClick={onRowClick}
            onRowDoubleClick={onRowDoubleClick}
            actions={actions}
            breakpoint={breakpoint}
            hideableColumns={hideableColumns}
          />
        ))}
      </tbody>
    </table>
  );
}
